import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashCompact from 'lodash/fp/compact';

import { USER_ROLE_ADMIN, USER_ROLE_PARTNER } from 'common/userRoles';

import { SR_API_ACTION_PARTNER_GET_BY_SLUG } from 'api/actionTypes';

import { SR_API_ACTION_UTILITY_PARTNER_GET_BY_SLUG } from 'api/utilityActionTypes';

import { getSunroomUtilityPortalUrl } from 'common/partnerHelpers';

import branch from 'client/core/branch';
import withScreenId from 'client/helpers/withScreenId';
import withTransitionHook from 'client/helpers/withTransitionHook';
import allowedRoles from 'client/helpers/allowedRoles';
import { withUtilityApiState } from 'client/helpers/withApiState';
import { SCREEN_ID_SUNROOM_UTILITY_PARTNER_OVERVIEW } from 'client/constants/screenIds';

import PartnerActions from 'client/actions/Partner';
import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityPartnerReferralActions from 'client/actions/UtilityPartnerReferral';
import UtilityActivationActions from 'client/actions/UtilityActivation';
import UtilityPayoutActions from 'client/actions/UtilityPayout';
import MetabaseChartActions from 'client/actions/MetabaseChart';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrPartnerSection from 'client/components/SrPartnerSection';
import UPActivationLink from 'client/components/UPActivationLink/UPActivationLink';
import UPReferralLink from 'client/components/UPReferralLink/UPReferralLink';
import UPActivationsSummary from 'client/components/UPActivationsSummary/UPActivationsSummary';
import SrUtilityProfitActivationsDataTable from 'client/components/SrUtilityProfitActivationsDataTable/SrUtilityProfitActivationsDataTable';
import SrUtilityProfitAddressesDataTable from 'client/components/SrUtilityProfitAddressesDataTable/SrUtilityProfitAddressesDataTable';

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_SUNROOM_UTILITY_PARTNER_OVERVIEW)
@withUtilityApiState(
  {
    getUtilityPartnerApiState: {
      action: SR_API_ACTION_UTILITY_PARTNER_GET_BY_SLUG,
    },
    getSunroomPartnerApiState: {
      action: SR_API_ACTION_PARTNER_GET_BY_SLUG,
    },
  },
  {},
)
@branch(({ match }) => ({
  currentUser: ['currentUser'],
  srPartner: ['partners', 'bySlug', match.params.partnerSlug],
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  utilityPayouts: ['utilityPayouts', 'pendingPayouts', 'byPartnerSlug', match.params.partnerSlug],
  utilityPartnerStripeAccounts: [
    'utilityPartners',
    'stripeAccountsByPartnerSlug',
    match.params.partnerSlug,
  ],
  utilityTypes: ['utilityActivations', 'types'],
  revenueDetails: ['utilityPartners', 'revenueDetailsBySlug', match.params.partnerSlug],
}))
@branch(({ srPartner }) => {
  if (!srPartner) {
    return {};
  }
  return {
    metabaseCharts: ['metabaseCharts', 'byPartnerId', srPartner.id],
  };
})
@branch(({ utilityPartner }) => {
  if (!utilityPartner) {
    return {};
  }
  return {
    referralEarnings: ['utilityPartners', 'referralEarnings', utilityPartner.id],
  };
})
class SunroomUtilityPartnerOverviewPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Profit Platform',
  });

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    srPartner: PropTypes.object,
    utilityPartner: PropTypes.object,
    utilityTypes: PropTypes.array,
    revenueDetails: PropTypes.number,
    getUtilityPartnerApiState: PropTypes.object.isRequired,
    getSunroomPartnerApiState: PropTypes.object.isRequired,
    metabaseCharts: PropTypes.array,
  };

  static fetchData(match, inject) {
    return Promise.all([
      UtilityPartnerActions.getBySlug(inject, {
        slug: match.params.partnerSlug,
      }).then((utilityPartner) => {
        if (utilityPartner) {
          return UtilityPartnerReferralActions.getPartnerEarnings(inject, {
            utilityPartnerId: utilityPartner.id,
            forceRefresh: true,
          });
        }
      }),
      UtilityPartnerActions.getStripeAccounts(inject, {
        slug: match.params.partnerSlug,
      }),
      UtilityPayoutActions.getPendingPayoutsByPartnerSlug(inject, {
        slug: match.params.partnerSlug,
      }),
      UtilityPartnerActions.getRevenueDetails(inject, {
        slug: match.params.partnerSlug,
        forceRefresh: true,
      }),
      UtilityActivationActions.getTypesList(inject),
      PartnerActions.loadPartnerBySlug(inject, {
        slug: match.params.partnerSlug,
      }).then((partner) => {
        if (partner) {
          return MetabaseChartActions.getByPartnerId(inject, {
            partnerId: partner.id,
          });
        }
      }),
    ]);
  }

  render() {
    const {
      currentUser,
      srPartner,
      utilityPartner,
      utilityTypes,
      metabaseCharts,
      referralEarnings,
      revenueDetails,
      isTransitioning,
      getUtilityPartnerApiState,
      getSunroomPartnerApiState,
      utilityPartnerStripeAccounts,
      utilityPayouts,
    } = this.props;

    if (
      isTransitioning ||
      getUtilityPartnerApiState.inProgress ||
      getSunroomPartnerApiState.inProgress
    ) {
      return <div className="loading-ellipses" />;
    }

    if (!srPartner) {
      return <Redirect to="/" />;
    }

    const navbarLinks = lodashCompact([
      {
        label: 'Home',
        value: `/partner/${srPartner?.slug}`,
      },
      metabaseCharts?.length > 0 && {
        label: 'Reports',
        value: `/partner/${srPartner?.slug}/reports`,
      },
      {
        label: 'Utilities',
        value: getSunroomUtilityPortalUrl(srPartner),
      },
    ]);

    return (
      <div className="SrPartnerPage">
        <SrPartnerPageV2
          navbarTitle={srPartner.displayName}
          navbarImage={srPartner.logoUrl}
          navbarImageLink={`/partner/${srPartner.slug}`}
          navbarLinks={navbarLinks}
          padding="0"
          contentPadding="0"
          maxWidth="1140px"
          navbarMaxWidth="1180px"
          currentUser={currentUser}
          currentPartner={srPartner}
          redirectToLastLocation={false}
        >
          <div className="SrPartnerPageContent">
            <SrPartnerSection padding="28px 0 0">
              {utilityPartner && (
                <>
                  <SrBox display="flex" justifyContent="space-between" gap="20px">
                    <UPActivationLink maxWidth="calc(50% - 10px)" utilityPartner={utilityPartner} />
                    <UPReferralLink
                      maxWidth="calc(50% - 10px)"
                      referralEarnings={referralEarnings || 0}
                      utilityPartner={utilityPartner}
                    />
                  </SrBox>
                  <UPActivationsSummary
                    isPartnerAccounting
                    utilityPartner={utilityPartner}
                    utilityPayouts={utilityPayouts}
                    collectedRevenue={revenueDetails?.totalCashOutAmount || 0}
                    expectedRevenue={revenueDetails?.expectedRevenueAmount || 0}
                    utilityPartnerStripeAccounts={utilityPartnerStripeAccounts}
                  />
                </>
              )}
              <SrUtilityProfitActivationsDataTable
                utilityTypes={utilityTypes || []}
                utilityPartner={utilityPartner}
              />
              <SrBox paddingBottom="80px">
                <SrUtilityProfitAddressesDataTable
                  title="Synced Listings"
                  utilityTypes={utilityTypes || []}
                  utilityPartnerId={utilityPartner?.id}
                  utilityPartnerSlug={utilityPartner?.slug}
                  liabilityInsuranceEnabled={
                    utilityPartner?.experienceConfiguration?.liabilityInsuranceEnabled
                  }
                />
              </SrBox>
            </SrPartnerSection>
          </div>
        </SrPartnerPageV2>
      </div>
    );
  }
}

SunroomUtilityPartnerOverviewPage.displayName = 'SunroomUtilityPartnerOverviewPage';

export default SunroomUtilityPartnerOverviewPage;
